import { computed, reactive, ref, type Ref } from "vue";
import type { FormContext } from "vee-validate";
import { ShopListInput } from "@/generated/graphql";
import { localStorageGetAuthType, localStorageGetChainCode, localStorageGetCustomerCode, localStorageGetGroup } from "@/common/localStorageUtil";
import { getShopList } from "@/common/graphql/shop";
import { checkChainListDisplay, isOperatorGroupAdmin, isOperatorGroupOther, isOperatorGroupShop } from "@/common/business";
import { OperationAuthTypeList, OperationGroupList } from "@/common/constant";

/**
 * 検索・絞り込みで使用する選択肢データを提供する composable。
 *
 * @returns 画面フォームで利用する状態と操作関数
 */
export const useOperatorListFilterOptions = (
  errdialog: Ref<boolean>,
  errMessage: Ref<string>,
) => {
  const chainViewFlag = checkChainListDisplay(localStorageGetAuthType());

  const chainListItems = ref<Array<{ chain_code: string; name: string }>>([]);
  const chainSelectValues = ref<{ chain_code: string; name: string } | null>({ chain_code: "", name: "" });
  const shopSelectValues = ref<{ shop_code: string; name: string } | null>({ shop_code: "", name: "" });
  const shopListItems = ref<Array<{ shop_code: string; name: string }>>([]);

  // 選択された値を格納する変数
  const groupSelectValues = ref<{ index: string; item: string } | null>(null);
  const authtypeSelectValues = ref<{ index: string; item: string } | null>(null);

  // 所属グループの状態
  const isOperatorGroupShopFlag = isOperatorGroupShop(localStorageGetGroup());
  const isOperatorGroupAdminFlag = isOperatorGroupAdmin(localStorageGetGroup());
  const isOperatorGroupOtherFlag = isOperatorGroupOther(localStorageGetGroup());

  const shopListInput = reactive<ShopListInput>({
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    limit: 10000,
    offset: 0,
  });

  // radio, select 設定
  const groupRadioItems = ref<Array<{ index: string; item: string }>>([{ index: "0", item: "" }]);
  const groupRadioItemsAll = OperationGroupList.other;
  const groupRadioItemsAdmin = OperationGroupList.shop_admin;
  const groupRadioItemsShop = OperationGroupList.shop;

  const authtypeRadioItems = ref<Array<{ index: string; item: string }>>([{ index: "0", item: "" }]);
  const authtypeRadioItemsOther = OperationAuthTypeList.other;
  const authtypeRadioItemsAdmin = OperationAuthTypeList.admin;
  const authtypeRadioItemsShop = OperationAuthTypeList.shop;
  const authtypeRadioItemsAll = OperationAuthTypeList.all;

  const searchFormRef = ref<FormContext | null>(null);

  // Form の初期値（マウント・再マウント時に vee-validate Field へ反映される）
  const formInitialValues = computed(() => ({
    chainName: chainSelectValues.value,
    shopName: shopSelectValues.value,
    operatorGroup: groupSelectValues.value,
    authType: authtypeSelectValues.value,
  }));

  const isSearchDisabled = computed(() => {
    if (chainViewFlag && (!chainSelectValues.value || chainSelectValues.value.chain_code === "")) {
      return true;
    }
    if (!groupSelectValues.value?.index) return true;
    if (!authtypeSelectValues.value?.index) return true;
    return false;
  });

  /**
   * 「チェーン」選択ボックス @update:modelValue
   */
  async function onChangeChain(): Promise<void> {
    try {
      if (!chainSelectValues.value) {
        shopListItems.value = [{ shop_code: "", name: "" }];
        shopSelectValues.value = { shop_code: "", name: "" };
        searchFormRef.value?.setFieldValue("shopName", null);
        return;
      }
      if (shopListInput.chain_code !== chainSelectValues.value.chain_code) {
        shopSelectValues.value = { shop_code: "", name: "" };
        searchFormRef.value?.setFieldValue("shopName", null);
      }
      shopListInput.chain_code = chainSelectValues.value.chain_code;
      shopListItems.value = await getShopList(shopListInput);
    } catch (err) {
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
        console.error("未知のエラー:", err);
      }
      return;
    }
  }

  /**
   * 所属グループクリック @click
   */
  function onClickGroup(): void {
    const selectedGroupIndex = groupSelectValues.value?.index;

    if (!selectedGroupIndex) {
      authtypeRadioItems.value = [];
      authtypeSelectValues.value = null;
      searchFormRef.value?.setFieldValue("authType", null);
      return;
    }

    if (isOperatorGroupShop(selectedGroupIndex)) {
      authtypeRadioItems.value = authtypeRadioItemsShop;
    } else if (isOperatorGroupAdmin(selectedGroupIndex)) {
      authtypeRadioItems.value = authtypeRadioItemsAdmin;
    } else {
      authtypeRadioItems.value = authtypeRadioItemsOther;
    }
    authtypeSelectValues.value = authtypeRadioItems.value[0] ?? null;
    searchFormRef.value?.setFieldValue("authType", authtypeSelectValues.value);
    searchFormRef.value?.validateField("authType");
  }

  return {
    chainViewFlag,
    chainListItems,
    chainSelectValues,
    shopSelectValues,
    shopListItems,
    shopListInput,
    groupSelectValues,
    authtypeSelectValues,
    isOperatorGroupShopFlag,
    isOperatorGroupAdminFlag,
    isOperatorGroupOtherFlag,
    groupRadioItems,
    groupRadioItemsAll,
    groupRadioItemsAdmin,
    groupRadioItemsShop,
    authtypeRadioItems,
    authtypeRadioItemsAll,
    searchFormRef,
    formInitialValues,
    isSearchDisabled,
    onChangeChain,
    onClickGroup,
  };
};
